import React, { Fragment, ReactNode, useMemo, useRef } from 'react'
import { isEmpty } from 'lodash'

import { getVCardSocial } from '@/constants/VCard'
import { INDUSTRY_CONFIG } from '@/constants/company'
import { IndustryT } from '@/lib/types/CompanyT'
import { VCardFileLink, VCardPreviewType } from '@/lib/types/VCardT'
import { cn, formatPhoneNumber, getWordInitials } from '@/lib/utils'
import { buildAddressString } from '@/lib/helpers/buildAddressString'
import { useScrollToNewItem } from '@/lib/hooks/useScrollToNewItem'
import useDominantImageColor from '@/lib/hooks/useDominantImageColor'
import { onColor } from '@/lib/color/contrast'
import Image from '@/components/Image'
import LcIcon from '@/components/livecontact/icons'
import InfoCard from '@/components/IOSContactPreview/components/InfoCard'

type Props = {
  data: VCardPreviewType | null
  /** Set for the landing-page hero preview only — gives the avatar fetch priority
   * and lets next/image optimize it, since that avatar is a known local asset.
   * Left off elsewhere because `data.avatar` can be an arbitrary/unconfigured
   * remote URL (dashboard, PDF preview). */
  isPriority?: boolean
}

/**
 * SF Pro Rounded is what the design uses for the contact name; `ui-rounded` resolves to it on
 * Apple platforms and falls back to the app stack everywhere else.
 */
const SF_ROUNDED =
  'ui-rounded, "SF Pro Rounded", -apple-system, BlinkMacSystemFont, system-ui, sans-serif'

/**
 * SF Symbols can't be shipped as assets, so the four quick-action glyphs are drawn to match the
 * filled symbols in the design: message.fill, phone.fill, video.fill, envelope.fill.
 */
const ACTION_GLYPHS: { key: string; paths: ReactNode }[] = [
  {
    key: 'message',
    paths: (
      <>
        <ellipse cx="12" cy="10.9" rx="9" ry="7.4" />
        <path d="M8.1 16.1c.5 1.9-.5 3.6-2.2 4.6-.4.2-.2.8.2.8 2.9-.2 5.5-1.6 7-3.6l-5-1.8Z" />
      </>
    ),
  },
  {
    key: 'call',
    paths: (
      <path d="M8.2 3.6c-.7-.7-1.8-.7-2.5 0L4.2 5.1C2.8 6.5 2.6 8.4 3.5 10.1c1.2 2.4 2.9 4.6 4.9 6.6 2 2 4.2 3.7 6.6 4.9 1.7.9 3.6.7 5-.7l1.5-1.5c.7-.7.7-1.8 0-2.5l-3-3c-.7-.7-1.8-.7-2.5 0l-1.1 1.1c-.3.3-.8.4-1.2.2-1-.6-2-1.3-2.8-2.1-.8-.8-1.5-1.8-2.1-2.8-.2-.4-.1-.9.2-1.2l1.1-1.1c.7-.7.7-1.8 0-2.5L8.2 3.6Z" />
    ),
  },
  {
    key: 'video',
    paths: (
      <>
        <path d="M4.8 5.5h7.7c1.5 0 2.8 1.3 2.8 2.8v7.4c0 1.5-1.3 2.8-2.8 2.8H4.8c-1.5 0-2.8-1.3-2.8-2.8V8.3c0-1.5 1.3-2.8 2.8-2.8Z" />
        <path d="M16.8 10.4l3.6-2.5c.7-.5 1.6 0 1.6.9v6.4c0 .9-.9 1.4-1.6.9l-3.6-2.5v-3.2Z" />
      </>
    ),
  },
  {
    key: 'mail',
    paths: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.2 5h13.6C20.6 5 22 6.4 22 8.2v7.6c0 1.8-1.4 3.2-3.2 3.2H5.2C3.4 19 2 17.6 2 15.8V8.2C2 6.4 3.4 5 5.2 5Zm.3 2.2c-.4 0-.6.5-.3.8l5.6 4.4c.7.5 1.7.5 2.4 0l5.6-4.4c.3-.3.1-.8-.3-.8H5.5Z"
      />
    ),
  },
]

const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

const toRgb = (color?: string | null): [number, number, number] | null => {
  const match = color?.trim().match(HEX_PATTERN)
  if (!match) return null

  const hex = match[1].length === 3 ? match[1].replace(/./g, (char) => char + char) : match[1]
  const value = parseInt(hex, 16)

  return [(value >> 16) & 255, (value >> 8) & 255, value & 255]
}

/**
 * The sheet background is a desaturated wallpaper, not the raw brand colour: 10% of the card
 * colour over a neutral, which reproduces the design's #8D8EA2 → #323447 ramp exactly.
 * Computed in JS rather than with `color-mix()` so the PDF/print capture renders it too.
 */
const wallpaperStop = (rgb: [number, number, number], base: [number, number, number]) =>
  `rgb(${base.map((channel, index) => Math.round(channel + (rgb[index] - channel) * 0.1)).join(', ')})`

type SheetRow = {
  key: string
  itemId: string
  label: string
  value: ReactNode
  href?: string
  external?: boolean
  multiline?: boolean
}

/**
 * iOS 17 contact sheet. Every metric comes from the design's 402×874pt screen rendered into the
 * 300px-wide phone screen (1pt ≈ 0.746px): 62pt status bar, 54pt toolbar, 150pt photo circle in a
 * 215pt photo block, 68pt quick actions, 16pt list margins, 24pt group radius, 52pt cells on 22pt
 * leading.
 * The dark iOS surfaces are intentional fixed colours — this is a picture of iOS, so it must not
 * follow the app's light/dark tokens.
 */
const IOSContactPreview = ({ data, isPriority }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const hasRealAvatar = Boolean(data?.avatar) && !data?.isAvatarGenerated
  const avatarColor = useDominantImageColor(hasRealAvatar ? data?.avatar : null)
  const primaryColor = useMemo(
    () => avatarColor || data?.mainColor || '#bfbfc3',
    [avatarColor, data?.mainColor]
  )
  /** The photo tile keeps the card's own colour even when the wallpaper follows the avatar. */
  const brandColor = useMemo(() => data?.mainColor || primaryColor, [data?.mainColor, primaryColor])
  /** The photo tile takes the brand at full strength, so its initials follow the fill. */
  const initialsColor = useMemo(() => onColor(brandColor), [brandColor])
  const wallpaper = useMemo(() => {
    const rgb = toRgb(primaryColor) ?? [191, 191, 195]

    return `linear-gradient(180deg, ${wallpaperStop(rgb, [147, 151, 152])} 0%, ${wallpaperStop(
      rgb,
      [46, 51, 51]
    )} 100%)`
  }, [primaryColor])

  const validSocials = useMemo(
    () => data?.socialLinks?.filter(({ url }) => Boolean(url?.trim())) ?? [],
    [data]
  )
  const fullName = useMemo(() => `${data?.firstName ?? ''} ${data?.lastName ?? ''}`.trim(), [data])
  const validDefaultLinks = useMemo(
    () =>
      data?.defaultLinks?.filter(({ url, fileId }) => Boolean(url?.trim()) || Boolean(fileId)) ??
      [],
    [data]
  )
  const validUnverifiedPhones = useMemo(
    () => data?.unverifiedPhones?.filter(({ phone }) => Boolean(phone?.trim())) ?? [],
    [data]
  )
  const validEmails = useMemo(
    () => data?.emails?.filter(({ value }) => Boolean(value?.trim())) ?? [],
    [data]
  )
  const validFileLinks = useMemo(() => (data?.companyFileItems ?? []) as VCardFileLink[], [data])
  const validAddresses = useMemo(
    () =>
      data?.addresses?.filter((address) =>
        Boolean(
          address.street1?.trim() ||
          address.street2?.trim() ||
          address.city?.trim() ||
          address.state?.trim() ||
          address.postal?.trim()
        )
      ) ?? [],
    [data?.addresses]
  )
  const fullDescription = useMemo(() => {
    if (!data?.notes) return ''

    const industryLabel = data?.industry
      ? INDUSTRY_CONFIG[data.industry as IndustryT]?.label || data.industry
      : ''

    if (isEmpty(data.taxonomy)) return data.notes
    return `${data.notes} \n\nFiled as: ${industryLabel}, ${data.taxonomy?.join(', ')}`
  }, [data?.notes, data?.taxonomy, data?.industry])

  const trackedItems = useMemo(
    () => [
      ...(data
        ? [
            { id: 'name', value: fullName },
            { id: 'industry', value: data.industry },
            { id: 'representativeName', value: data.representativeName },
            { id: 'avatar', value: [data.avatar, data.isAvatarGenerated] },
            { id: 'brandColor', value: data.mainColor, scrollToTop: true },
          ]
        : []),
      ...(data?.website ? [{ id: 'website', value: data.website }] : []),
      ...(data?.notes ? [{ id: 'notes', value: data.notes }] : []),
      ...validSocials.map(({ type, url }) => ({ id: `social:${type}`, value: url })),
      ...validUnverifiedPhones.map(({ phone, label }) => ({ id: `phone:${phone}`, value: label })),
      ...validEmails.map(({ value, alias }) => ({ id: `email:${value}`, value: alias })),
      ...validAddresses.map((address) => ({
        id: `address:${buildAddressString(address)}`,
        value: address.label,
      })),
      ...validDefaultLinks.map(({ clientType, label, url, file }) => ({
        id: `link:${clientType}`,
        value: `${label ?? ''}|${url || file?.permanentUrl || ''}`,
      })),
      ...validFileLinks.map((f) => ({
        id: `file:${f.id}`,
        value: `${f.name}|${f.file?.permanentUrl ?? ''}`,
      })),
    ],
    [
      data,
      validSocials,
      validUnverifiedPhones,
      validEmails,
      validAddresses,
      validDefaultLinks,
      validFileLinks,
      fullName,
    ]
  )
  const { registerItem } = useScrollToNewItem(trackedItems, { containerRef })

  /** One grouped card holds every labelled field, exactly as the design lays it out. */
  const rows = useMemo<SheetRow[]>(() => {
    const list: SheetRow[] = []

    validUnverifiedPhones.forEach(({ phone, label }, index) =>
      list.push({
        key: `phone-${index}`,
        itemId: `phone:${phone}`,
        label: label || 'phone',
        value: formatPhoneNumber(phone),
        href: `tel:${phone}`,
      })
    )

    validEmails.forEach(({ value, alias }, index) =>
      list.push({
        key: `email-${index}`,
        itemId: `email:${value}`,
        label: alias || 'email',
        value,
        href: `mailto:${value}`,
      })
    )

    validDefaultLinks.forEach(({ url, label, file, clientType }, index) => {
      const href = url || file?.permanentUrl || ''

      list.push({
        key: `link-${index}`,
        itemId: `link:${clientType}`,
        label: label || 'link',
        value: href,
        href,
        external: true,
      })
    })

    if (data?.website) {
      list.push({
        key: 'website',
        itemId: 'website',
        label: 'website',
        value: data.website,
        href: data.website,
        external: true,
      })
    }

    validSocials.forEach(({ type, url }, index) => {
      const field = getVCardSocial(type)
      if (!field || !url) return

      list.push({
        key: `social-${index}`,
        itemId: `social:${type}`,
        label: field.name || '-',
        value: url,
        href: url,
        external: true,
      })
    })

    validFileLinks.forEach((fileLink) =>
      list.push({
        key: `file-${fileLink.id}`,
        itemId: `file:${fileLink.id}`,
        label: fileLink.name,
        value: fileLink.file?.permanentUrl,
        href: fileLink.file?.permanentUrl,
        external: true,
      })
    )

    validAddresses.forEach((address, index) =>
      list.push({
        key: `address-${index}`,
        itemId: `address:${buildAddressString(address)}`,
        label: address.label || 'address',
        value: buildAddressString(address),
        multiline: true,
      })
    )

    if (data?.notes) {
      list.push({
        key: 'notes',
        itemId: 'notes',
        label: 'Notes',
        value: fullDescription,
        multiline: true,
      })
    }

    return list
  }, [
    data?.website,
    data?.notes,
    fullDescription,
    validUnverifiedPhones,
    validEmails,
    validDefaultLinks,
    validSocials,
    validFileLinks,
    validAddresses,
  ])

  const initials = getWordInitials(fullName || data?.representativeName)

  /** Contact photo: the card colour fills the circle, so logos with transparency sit on brand. */
  const photo = (size: number, textSize: string) =>
    hasRealAvatar ? (
      <Image
        src={data?.avatar ?? ''}
        alt={`${data?.representativeName} avatar`}
        width={size * 2}
        height={size * 2}
        preload={isPriority}
        unoptimized={!isPriority}
        className="h-full w-full object-cover"
      />
    ) : (
      <span
        className={cn('font-semibold leading-none tracking-[-0.5px]', textSize)}
        style={{ fontFamily: SF_ROUNDED, color: initialsColor }}
      >
        {initials}
      </span>
    )

  return (
    <div
      ref={containerRef}
      className="lc-asset-preview lc-scrollbar-none flex h-full w-full flex-col overflow-y-auto font-lc text-white"
      style={{ background: wallpaper }}
    >
      {/* 62pt status bar lives in the phone frame; the sheet starts under it. */}
      <div className="flex grow flex-col pb-[14px] pt-[46px]">
        {/* Toolbar — 54pt tall, back and Edit on the 16pt margin */}
        <div aria-hidden className="flex h-[40px] shrink-0 items-center justify-between px-[12px]">
          <span className="inline-flex size-[33px] items-center justify-center rounded-full bg-white/[0.58] text-black/85 backdrop-blur-[12px]">
            <LcIcon name="chevron-left" size={16} strokeWidth={2.5} className="-ml-[1px]" />
          </span>
          <span className="inline-flex h-[33px] items-center rounded-full bg-white/[0.58] px-[11px] text-[12.7px] leading-none tracking-[-0.32px] text-black/85 backdrop-blur-[12px]">
            Edit
          </span>
        </div>

        {/* Photo, name, quick actions */}
        <div className="flex shrink-0 flex-col items-center px-[12px]">
          <div
            ref={registerItem('avatar')}
            className="mt-[18px] flex size-[112px] shrink-0 items-center justify-center overflow-hidden rounded-full"
            style={{ backgroundColor: brandColor }}
          >
            {photo(112, 'text-[38px]')}
          </div>

          <h1
            ref={registerItem('name')}
            className="mt-[16px] w-full truncate px-[8px] text-center text-[28px] font-bold leading-[31px] tracking-[-0.64px] text-white"
            style={{ fontFamily: SF_ROUNDED }}
          >
            {fullName || data?.representativeName || '-'}
          </h1>
          {fullName && (
            <p
              ref={registerItem('representativeName')}
              className="w-full truncate px-[8px] text-center text-[12.7px] leading-[16.4px] tracking-[-0.32px] text-white/70"
            >
              {data?.representativeName}
            </p>
          )}

          <div aria-hidden className="mt-[13px] flex items-center gap-[7.5px]">
            {ACTION_GLYPHS.map(({ key, paths }) => (
              <span
                key={key}
                className="flex size-[51px] items-center justify-center rounded-full bg-black/10"
              >
                <svg viewBox="0 0 24 24" className="size-[23px] fill-current">
                  {paths}
                </svg>
              </span>
            ))}
          </div>
        </div>

        <div className="mt-[10px] flex flex-col gap-[9px] px-[12px]">
          {/* Contact Photo & Poster — 75pt row with the brand-filled tile */}
          <div
            aria-hidden
            className="flex h-[56px] shrink-0 items-center gap-[7px] rounded-[18px] bg-black/10 pl-[9px] pr-[10px]"
          >
            <span
              className="flex size-[36px] shrink-0 items-center justify-center overflow-hidden rounded-full"
              style={{ backgroundColor: brandColor }}
            >
              {photo(36, 'text-[13px]')}
            </span>
            <p className="min-w-0 flex-1 truncate text-[12.7px] font-medium leading-[16.4px] tracking-[-0.32px] text-white/90">
              Contact Photo &amp; Poster
            </p>
            <LcIcon
              name="chevron-right"
              size={13}
              strokeWidth={2.5}
              className="shrink-0 text-white/60"
            />
          </div>

          {/* Grouped list — 24pt radius, 16pt insets, hairlines between cells */}
          {!isEmpty(rows) && (
            <div className="flex flex-col gap-[7.5px] rounded-[18px] bg-black/10 px-[12px] py-[7.5px]">
              {rows.map(({ key, itemId, label, value, href, external, multiline }, index) => (
                <Fragment key={key}>
                  {index > 0 && (
                    <span aria-hidden className="h-px w-full shrink-0 bg-white/[0.18]" />
                  )}
                  <InfoCard
                    ref={registerItem(itemId)}
                    label={label}
                    value={value}
                    href={href}
                    external={external}
                    multiline={multiline}
                  />
                </Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default IOSContactPreview
